import { useParams } from "react-router-dom";

import { tierHistoryOf, type McpTierHistory } from "../../api/mcp";
import { Breadcrumbs } from "../../components/Layout";
import {
  CouldNotLook,
  Denied,
  LoadFailed,
  Loading,
  NotSeenRecently,
  SignedOut,
} from "../../components/states";
import { Badge, Empty, Page, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";

/** Every tier this merchant has been on, newest first. */
export function TenantHistory({ config }: { config: AppConfig }) {
  const { ref = "" } = useParams();
  const path = `/mcp/admin/tenant/${encodeURIComponent(ref)}/history`;
  const { state, reload } = useResource<McpTierHistory>(config, path, [ref]);

  return (
    <Page title={<span className="ref">{ref}</span>} subtitle="Tier history.">
      <Breadcrumbs
        trail={[
          { label: "Overview", to: "/" },
          { label: "Tenants", to: "/tenants" },
          { label: ref, to: `/tenants/${encodeURIComponent(ref)}` },
          { label: "History" },
        ]}
      />

      {state.kind === "loading" && <Loading what="history" />}
      {state.kind === "signedout" && <SignedOut />}
      {state.kind === "denied" && <Denied message={state.error.message} />}
      {state.kind === "notfound" && <NotSeenRecently reference={ref} />}
      {state.kind === "unavailable" && (
        <CouldNotLook what="history" message={state.error.message} onRetry={reload} />
      )}
      {state.kind === "invalid" && (
        <LoadFailed what="history" message={state.error.message} />
      )}
      {state.kind === "error" && <LoadFailed what="history" message={state.error.message} />}

      {state.kind === "ready" && tierHistoryOf(state.data).length === 0 && (
        <Empty>No tier has ever been recorded for this merchant.</Empty>
      )}

      {state.kind === "ready" && tierHistoryOf(state.data).length > 0 && (
        <Table
          head={
            <tr>
              <Th>Tier</Th>
              <Th>Set by</Th>
              <Th>When</Th>
            </tr>
          }
        >
          {tierHistoryOf(state.data).map((h, i) => (
            <tr key={`${h.created_at}-${i}`}>
              <Td>
                <Badge>{h.tier}</Badge>
              </Td>
              <Td>
                {/* "signup" means the row was created automatically on first
                    use — not a person, and saying "signup" reads as one. */}
                {h.source === null ? (
                  <span className="text-slate-500 dark:text-slate-400">unknown</span>
                ) : h.source === "signup" ? (
                  <span className="text-slate-500 dark:text-slate-400">automatic — first use</span>
                ) : (
                  <span className="ref text-xs">{h.source.replace(/^admin:/, "")}</span>
                )}
              </Td>
              <Td>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {h.created_at ? new Date(h.created_at).toLocaleString() : "—"}
                </span>
              </Td>
            </tr>
          ))}
        </Table>
      )}
    </Page>
  );
}
